import { useCallback, useEffect, useState } from 'react'
import {
  describeDaysLeft,
  describeKind,
  loadTrash,
  restoreFromTrash,
  type TrashItem,
} from '@core/trash'
import { Button, Card, ErrorNote, Spinner } from '@design/components'
import { Overlay } from '@design/Overlay'

/**
 * The bin.
 *
 * Every row says how long it has left, in days, in words. "Nog 12 dagen terug
 * te halen" is a promise; a deletion date in small print is a trap. Nothing
 * here offers to empty the bin early — the thirty days are the whole point,
 * and a "delete permanently" button is a way to lose something at the exact
 * moment you are annoyed enough to press it.
 */
export function TrashScreen({ onClose }: { onClose: () => void }) {
  const [items, setItems] = useState<TrashItem[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')

  const refresh = useCallback(() => {
    loadTrash()
      .then(setItems)
      .catch(() => setItems([]))
  }, [])

  useEffect(refresh, [refresh])

  async function restore(item: TrashItem) {
    setBusy(item.id)
    setError('')
    try {
      await restoreFromTrash(item.kind, item.id)
      refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Terughalen lukte niet.')
    }
    setBusy(null)
  }

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <span className="font-medium px-2">Prullenbak</span>
        <button onClick={onClose} className="text-muted px-2 py-2">
          Klaar
        </button>
      </header>

      <div className="flex-1 scroll-area px-4 py-4 pad-safe-bottom">
        {!items && <Spinner label="Even geduld…" />}

        {items && items.length === 0 && (
          <Card className="text-center py-8">
            <p className="font-medium mb-1">De prullenbak is leeg</p>
            <p className="text-sm text-muted">
              Wat je weggooit blijft hier 30 dagen staan.
            </p>
          </Card>
        )}

        <ErrorNote>{error}</ErrorNote>

        {(items ?? []).map((item) => (
          <Card key={`${item.kind}-${item.id}`} className="mb-2">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.title}</p>
                <p className="text-sm text-muted">
                  {describeKind(item.kind)} · {describeDaysLeft(item.purgesOn)}
                </p>
              </div>
              <button
                onClick={() => restore(item)}
                disabled={busy === item.id}
                className="shrink-0 text-sm underline underline-offset-2 py-1 px-1"
              >
                {busy === item.id ? '…' : 'Terug'}
              </button>
            </div>
          </Card>
        ))}
      </div>
    </Overlay>
  )
}

/**
 * The delete button itself, wherever something can be thrown away.
 *
 * Quiet by design, and it confirms in place rather than in a dialog — an
 * alert() blocks the whole app on iOS, and a second tap on the same button
 * says the same thing without moving anything under your thumb.
 */
export function TrashButton({
  onConfirm,
  label = 'Weggooien',
}: {
  onConfirm: () => Promise<void>
  label?: string
}) {
  const [armed, setArmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (!armed) {
    return (
      <button onClick={() => setArmed(true)} className="w-full py-2 text-sm text-muted">
        {label}
      </button>
    )
  }

  return (
    <div>
      <ErrorNote>{error}</ErrorNote>
      <p className="text-xs text-muted text-center mb-2">
        Het blijft 30 dagen in de prullenbak staan.
      </p>
      <div className="flex gap-2">
        <Button tone="quiet" onClick={() => setArmed(false)}>
          Toch niet
        </Button>
        <Button
          tone="danger"
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            setError('')
            try {
              await onConfirm()
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Verwijderen lukte niet.')
              setBusy(false)
              setArmed(false)
            }
          }}
        >
          {busy ? 'Bezig…' : 'Weggooien'}
        </Button>
      </div>
    </div>
  )
}
